// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useChainsQuery } from "@/api/hooks/use-chains";
import { DataTable } from "@/components/data-table";
import { ChainPlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { CopyableKey } from "@/components/shared/copyable-key";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { TimezoneCell } from "@/components/shared/timezone-cell";
import { Button } from "@/components/ui/button";
import { ChainActionsMenu } from "@/features/chains/chain-actions-menu";
import { CompareChainDialog } from "@/features/chains/compare-chains-dialog";
import { CreateChainDialog } from "@/features/chains/create-chain-dialog";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/chains/index";

interface ChainRow {
  chainId: number;
  chainName: string;
  legibleName: string;
  defaultTimeZone: string;
  storesCount: number;
  activeStoresCount: number;
  isActive: boolean;
}

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "legibleName",
  sortDesc: false,
};

export function ChainsList() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.chainsEdit);
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateFrom, setDuplicateFrom] = useState<{
    copyFrom: string;
    defaultTimeZone: string;
  }>();
  const [compareDialogChain, setCompareDialogChain] = useState<string | null>(null);

  const sortBy = (sorting[0]?.id ?? "legibleName") as
    | "legibleName"
    | "chainId"
    | "chainName"
    | "defaultTimeZone"
    | "storesCount"
    | "activeStoresCount"
    | "active";
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        onlyActive,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "legibleName",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/chains",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [search, onlyActive, pagination.pageIndex, pagination.pageSize, sorting, navigate],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const isFiltered = !!(search || !onlyActive);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: createDialogOpen || compareDialogChain !== null });

  const { data, isLoading, refetch, isFetching } = useChainsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/chains", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[onlyActive]": onlyActive || undefined,
        },
      },
    });
    const items = (res.data as unknown as { chains: ChainRow[] }).chains;
    downloadCsv(
      "chains.csv",
      ["ID", "Name", "Key", "Timezone", "Active Stores", "Total Stores", "Status"],
      items.map((c) => [
        String(c.chainId),
        c.legibleName,
        c.chainName,
        c.defaultTimeZone,
        String(c.activeStoresCount),
        String(c.storesCount),
        c.isActive ? "Active" : "Inactive",
      ]),
    );
  }, [search, sortBy, orderBy, onlyActive]);

  const columns = useMemo<ColumnDef<ChainRow, unknown>[]>(
    () => [
      {
        accessorKey: "chainId",
        header: t("chains.list.columns.id"),
        size: 80,
        enableSorting: true,
        enableHiding: false,
        meta: { columnLabel: t("chains.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.chainId} />,
      },
      {
        accessorKey: "legibleName",
        header: t("chains.list.columns.legibleName"),
        meta: { columnLabel: t("chains.list.columns.legibleName") },
        cell: ({ row, table }) => {
          // Surface the chain key as a sub-line when its dedicated column is hidden from the view.
          const keyColumnVisible = table.getColumn("chainName")?.getIsVisible() ?? false;
          return (
            <div className="flex flex-col">
              <ChainLink
                chainName={row.original.chainName}
                legibleName={row.original.legibleName}
                className="font-medium"
                showIcon={false}
              />
              {!keyColumnVisible && <CopyableKey value={row.original.chainName} />}
            </div>
          );
        },
      },
      {
        accessorKey: "chainName",
        header: t("chains.list.columns.chainName"),
        meta: { columnLabel: t("chains.list.columns.chainName") },
        cell: ({ row }) => <KeyChip label={row.original.chainName} variant="chain" />,
      },
      {
        accessorKey: "defaultTimeZone",
        header: t("chains.list.columns.timezone"),
        enableSorting: false,
        meta: { columnLabel: t("chains.list.columns.timezone") },
        cell: ({ row }) => <TimezoneCell timeZone={row.original.defaultTimeZone} />,
      },
      {
        accessorKey: "storesCount",
        header: t("chains.list.columns.numberOfStores"),
        enableSorting: true,
        meta: { columnLabel: t("chains.list.columns.numberOfStores") },
        cell: ({ row }) => (
          <span>
            {row.original.activeStoresCount} / {row.original.storesCount}
          </span>
        ),
      },
      {
        accessorKey: "isActive",
        header: t("chains.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("chains.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <ChainActionsMenu
            onDuplicate={() => {
              setDuplicateFrom({
                copyFrom: row.original.chainName,
                defaultTimeZone: row.original.defaultTimeZone,
              });
              setCreateDialogOpen(true);
            }}
            onCompareSettings={() => setCompareDialogChain(row.original.chainName)}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader title={t("chains.title")} />
      <div className="p-6">
        <DataTable
          storageKey="chains-list"
          columns={columns}
          data={data?.chains ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          onExport={handleExport}
          searchPlaceholder={t("chains.list.searchPlaceholder", "Search chains...")}
          filters={
            <>
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            canEdit ? (
              <Button
                onClick={() => {
                  setDuplicateFrom(undefined);
                  setCreateDialogOpen(true);
                }}
              >
                <ChainPlusIcon className="h-4 w-4" />
                {t("chains.list.addChainButton")}
              </Button>
            ) : null
          }
        />
      </div>

      <CreateChainDialog
        key={duplicateFrom?.copyFrom ?? "create"}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={duplicateFrom}
      />

      <CompareChainDialog
        defaultChainName={compareDialogChain ?? ""}
        open={compareDialogChain != null}
        onOpenChange={(open) => {
          if (!open) setCompareDialogChain(null);
        }}
      />
    </div>
  );
}
